import React, { useState } from 'react';
import useFetch from 'use-http';
import Button from 'components/UI/Button';
import { useNavigate } from 'react-router-dom';
import styles from './styles.module.scss';
import { $store } from 'models/store';
import { useUnit } from 'effector-react';
import moment from 'moment';
import { CLOSE, ISession, OPEN, PENDING, UNSUCCESSFUL } from 'types/session';
import Table from 'components/UI/Table';
import usePaginate from 'hooks/usePaginate';
import Paginate from 'components/UI/Paginate';
import Popup from 'components/Popup';
import { OutsourceRolesEnum } from 'types/users';
import { formatServiceTypeTitle, formatUserFullName } from 'utils/helpers';

interface CashierData {
	cashier_login: string;
	cashier_passw: string;
}

function displayStatus(row: ISession) {
	const { status } = row;
	switch (true) {
		case status === UNSUCCESSFUL:
			return 'Не состоялась';
		case status === PENDING:
			return 'В ожидании';
		case status === OPEN:
			return 'Открыта';
		case status === CLOSE:
			return 'Закрыта';
		default:
			return '';
	}
}

function displayPosition(row: any) {
	switch (true) {
		case !!row?.outsourcing?.pos?.title:
			return formatServiceTypeTitle(row?.outsourcing?.pos?.title);
		case !!row?.request?.position?.title:
			return formatServiceTypeTitle(row?.request?.position?.title);
		case !!row?.begin?.outsourcing?.position:
			return formatServiceTypeTitle(row?.begin?.outsourcing?.position);
		default:
			return '';
	}
}

function displayCounterParty(row: any) {
	switch (true) {
		case !!row?.outsourcing?.counterparty?.title:
			return row?.outsourcing?.counterparty?.title;
		case !!row?.begin?.outsourcing?.counterparty?.title:
			return row?.begin?.outsourcing?.counterparty?.title;
		case !!row?.request?.counterparty?.title:
			return row?.request?.counterparty?.title;
		default:
			return '';
	}
}

const perPage = 10;

// Функция для проверки разности времени смены
function shouldShowDurationButtons(row: ISession): boolean {
	if (!row.begin?.createdAt || !row.end?.createdAt || !row.outsourcing?.plannedStart || !row.outsourcing?.plannedEnd) {
		return false;
	}

	// Если часы уже подтверждены, не показываем кнопки
	if ((row as any).hoursApproved) {
		return false;
	}

	const actualDuration = moment(row.end.createdAt).diff(moment(row.begin?.createdAt), 'hours', true);
	const plannedDuration = moment(row.outsourcing?.plannedEnd).diff(
		moment(row.outsourcing?.plannedStart),
		'hours',
		true,
	);
	const difference = Math.abs(actualDuration - plannedDuration);

	return difference > 1.5;
}

function shouldHighlightRow(row: ISession, mode: string): boolean {
	if (mode !== 'report') {
		return false;
	}

	if (row.status !== CLOSE) {
		return false;
	}

	if (!row.outsourcing?.plannedStart || !row.outsourcing?.plannedEnd) {
		return false;
	}

	const plannedDuration = moment(row.outsourcing.plannedEnd).diff(moment(row.outsourcing.plannedStart), 'hours', true);

	const actualHours = (row as any).hours;

	const difference = Math.abs(plannedDuration - actualHours);

	return difference > 1.5 && !row.hoursApproved;
}

function CustomButtonRow({ row, openPopup }: { row: ISession; openPopup: (row: ISession) => void }) {
	const [isDisable, setIsDisable] = useState<boolean>(false);

	const hundleClick = (row: ISession) => {
		setIsDisable(true);
		openPopup(row);
	};

	React.useEffect(() => {
		if (!isDisable) return;

		const timer = setTimeout(() => {
			setIsDisable(false);
		}, 10000);

		return () => clearTimeout(timer);
	}, [isDisable]);

	const navigate = useNavigate();
	return (
		<div key={row.id}>
			<Button
				variant="green"
				onClick={() => navigate(`accept/${row.begin.outsourcing?.id}/close/-1/${row.id ? row.id : '-1'}`)}
			>
				Закрыть смену
			</Button>
			{(formatServiceTypeTitle(row?.begin?.outsourcing?.position) === OutsourceRolesEnum.Cashier ||
				formatServiceTypeTitle(row?.outsourcing?.pos?.title) === OutsourceRolesEnum.Cashier) && (
				<Button disabled={isDisable} variant="violet" onClick={() => hundleClick(row)}>
					Получить логин
				</Button>
			)}
		</div>
	);
}

export default function Request({
	status,
	mode = 'planned',
}: {
	status: string[];
	mode?: 'planned' | 'open' | 'report';
}) {
	const navigate = useNavigate();

	const store = useUnit($store);
	const { get, post, patch, loading } = useFetch();
	const [sessions, setSessions] = useState<ISession[]>([]);
	const [isLoadingSessions, setIsLoadingSessions] = useState(false);

	const { pageCount, offset, setCount, handlePageClick, currentPage } = usePaginate(perPage);

	const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
	const [data, setData] = useState<CashierData[] | []>([]);
	const [loading2, setLoading2] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);

	const handleApproveHours = async (sessionId: number) => {
		try {
			await patch(`/api/session/${sessionId}`, {
				hoursApproved: true,
			});

			// Обновляем конкретную сессию в локальном состоянии
			setSessions(prevSessions =>
				prevSessions.map(session => (session.id === sessionId ? { ...session, hoursApproved: true } : session)),
			);
		} catch (error) {
			console.error('Ошибка при подтверждении часов:', error);
		}
	};

	// Проверяем, все ли сессии подтверждены
	const areAllSessionsApproved = () => {
		return sessions.length > 0 && sessions.every(session => (session as any).hoursApproved);
	};

	const handleSendReport = async () => {
		if (!store?.id) {
			alert('Не выбран магазин');
			return;
		}

		setIsLoadingReport(true);
		try {
			const response = await post(`/api/session/send-report?storeId=${store.id}`);

			if (response.success) {
				const message = response.emailSent
					? `Отчет успешно создан и отправлен на email! Обработано записей: ${response.recordsCount}`
					: `Отчет успешно создан! Обработано записей: ${response.recordsCount} (email не настроен)`;
				alert(message);
			} else {
				alert('Ошибка при создании отчета');
			}
		} catch (error) {
			console.error('Ошибка при отправке отчета:', error);
			alert('Ошибка при отправке отчета');
		} finally {
			setIsLoadingReport(false);
		}
	};

	const openPopup = async (row: ISession) => {
		setLoading2(true);
		setError(null);
		try {
			const { firstname, secondname, middlename } = row.user;
			const data = {
				store: row.store?.number,
				cashier_type: '2',
				additional: [
					{
						auto_num: '',
						famname: firstname,
						name: secondname,
						surename: middlename ? middlename : '',
						company: row?.begin?.outsourcing?.counterparty?.title,
					},
				],
			};
			const response = await post('/api/outsourcing/cashierCredential', data);
			if (!response?.length) {
				throw new Error('Ошибка во время выполнения запроса');
			}
			const result: CashierData[] = response;
			setData(result);
		} catch (error) {
			setError('Ошибка во время выполнения запроса');
		} finally {
			setLoading2(false);
			setIsPopupOpen(true);
		}
	};
	const closePopup = () => {
		setIsPopupOpen(false);
		setData([]);
	};

	React.useEffect(() => {
		let timer: NodeJS.Timeout;

		if (isPopupOpen) {
			timer = setTimeout(() => {
				closePopup();
			}, 30000);
		}

		return () => {
			clearTimeout(timer);
		};
	}, [isPopupOpen]);

	const loadSession = React.useCallback(async () => {
		if (!store || isLoadingSessions) {
			return;
		}

		setIsLoadingSessions(true);

		// Очищаем предыдущие данные при новом запросе
		setSessions([]);
		setCount(0);

		let url = `api/session/findOutsourcing?offset=${offset}&limit=${perPage}&storeId=${store.id}&status=${status}`;
		if (Array.isArray(status) ? status.includes('pending') : String(status).includes('pending')) {
			url += `&sortField=$request.start$&sortOrder=desc`;
		}

		try {
			const sessionList = await get(url);
			if (sessionList?.rows?.length) {
				setSessions(sessionList.rows);
				setCount(sessionList.count);
			} else {
				setSessions([]);
				setCount(0);
			}
		} catch (error) {
			console.log('Ошибка загрузки сессий:', error);
			setSessions([]);
			setCount(0);
		} finally {
			setIsLoadingSessions(false);
		}
	}, [store, offset, status, get, setCount]);

	React.useEffect(() => {
		loadSession();
	}, [loadSession]);

	// Очищаем состояние при изменении режима
	React.useEffect(() => {
		setSessions([]);
		setCount(0);
		setIsLoadingSessions(false);
	}, [mode, status]);

	// Дополнительная очистка при размонтировании
	React.useEffect(() => {
		return () => {
			setSessions([]);
			setCount(0);
			setIsLoadingSessions(false);
		};
	}, []);

	const formatRequestId = React.useCallback((row: any) => {
		return <div>{row?.request?.request_id}</div>;
	}, []);

	const formatCounterparty = React.useCallback((row: any) => {
		return <div>{displayCounterParty(row)}</div>;
	}, []);

	const formatStore = React.useCallback((row: any) => {
		return <div>{row?.store?.title}</div>;
	}, []);

	const formatPosition = React.useCallback((row: any) => {
		return <div>{displayPosition(row)}</div>;
	}, []);

	const formatFio = React.useCallback((row: any) => {
		return <div>{formatUserFullName(row.user)}</div>;
	}, []);

	const formatHours = React.useCallback((row: any) => {
		const startSource = row.outsourcing?.plannedStart || row.request?.start;
		const endSource = row.outsourcing?.plannedEnd || row.request?.end;

		if (!startSource || !endSource) {
			return <div>--</div>;
		}

		return <div>{moment(endSource).diff(moment(startSource), 'hours')}</div>;
	}, []);

	const formatHoursPayable = React.useCallback((row: any) => {
		return <div>{row.hours}</div>;
	}, []);

	const formatTimeInterval = React.useCallback((row: any) => {
		let startTime = row.begin ? moment(row.begin.createdAt).format('DD.MM.YY HH:mm') : '--:--';
		let endTime = row.end ? moment(row.end.createdAt).format('HH:mm') : '--:--';
		if (mode === 'planned') {
			const startSource = row.outsourcing.plannedStart || row.request?.start;
			const endSource = row.outsourcing.plannedEnd || row.request?.end;

			startTime = startSource ? moment(startSource).format('DD.MM.YY HH:mm') : '--:--';
			endTime = endSource ? moment(endSource).format('HH:mm') : '--:--';
		}
		return (
			<div style={{ fontSize: '12px', lineHeight: '1.2' }}>
				<div>
					{startTime} - {endTime}
				</div>
			</div>
		);
	}, []);

	const formatStatus = React.useCallback((row: any) => {
		return <div>{displayStatus(row)}</div>;
	}, []);

	const formatOperation = React.useCallback(
		(row: any) => {
			if (row.status === UNSUCCESSFUL) {
				return <div></div>;
			}
			return (
				<div>
					{mode === 'planned' && row?.status === PENDING && (
						<div>
							{row.outsourcing && (
								<Button
									disabled={
										formatServiceTypeTitle(row.outsourcing.pos.title) === OutsourceRolesEnum.Cashier &&
										!(row.user?.document_number && row.user?.firstname && row.user?.secondname)
									}
									variant="green"
									onClick={() => navigate(`open/${row.id}/`)}
								>
									Открыть смену
								</Button>
							)}
							{(row.outsourcingId || row.outsourcing?.id) && (
								<Button variant="violet" onClick={() => navigate(`edit/${row.outsourcingId || row.outsourcing?.id}`)}>
									Изменить данные ИСИТ
								</Button>
							)}
						</div>
					)}
					{mode === 'open' && row?.status === OPEN && <CustomButtonRow row={row} openPopup={openPopup} />}
					{mode === 'report' && row?.status === CLOSE && (
						<div>
							{shouldShowDurationButtons(row) && (
								<Button variant="violet" onClick={() => navigate(`edit-session/${row.id}`)}>
									Изменить длительность смены
								</Button>
							)}

							{!row.hoursApproved && (
								<Button variant="green" onClick={() => handleApproveHours(row.id)}>
									Подтвердить длительность смены
								</Button>
							)}
						</div>
					)}
				</div>
			);
		},
		[navigate, openPopup, mode],
	);

	// Функция для стилизации строк
	const getRowStyle = React.useCallback(
		(row: ISession): React.CSSProperties => {
			if (shouldHighlightRow(row, mode)) {
				return {
					backgroundColor: 'rgb(240, 128, 128)',
					color: 'white',
				};
			}
			return {};
		},
		[mode],
	);

	const headers = [
		{
			value: 'id',
			visible: false,
		},
		{
			value: 'request_id',
			title: 'Номер заявки',
			width: '100px',
			formatted: formatRequestId,
		},
		{
			value: 'counterparty',
			title: 'Контрагент',
			width: 'min(280px, 42vw)',
			formatted: formatCounterparty,
			visible: mode === 'planned' || mode === 'open',
		},
		{
			value: 'outsourcePosition',
			title: 'Тип услуги',
			width: '100px',
			formatted: formatPosition,
		},
		{
			value: 'fio',
			title: 'ФИО',
			width: '100px',
			formatted: formatFio,
		},
		{
			value: 'hours',
			title: 'Кол-во ч. из заявки',
			width: '100px',
			formatted: formatHours,
		},
		{
			value: 'timeInterval',
			title: 'Время смены',
			width: '100px',
			formatted: formatTimeInterval,
		},
		{
			value: 'hoursPayable',
			title: 'Присутствие в магазине, ч.',
			formatted: formatHoursPayable,
			width: '100px',
			visible: mode === 'report',
		},
		{
			value: 'status',
			title: 'Статус смены',
			formatted: formatStatus,
			visible: mode === 'report' || mode === 'open',
		},
		{
			value: 'operation',
			title: 'Операции',
			formatted: formatOperation,
		},
	];

	return (
		<>
			<div className={styles['section-item']}>
				<Table headers={headers} rows={sessions} isLoading={loading || isLoadingSessions} getRowStyle={getRowStyle} />
			</div>
			{areAllSessionsApproved() && mode === 'report' && (
				<div className={styles['section-item']}>
					<Button variant="green" onClick={handleSendReport} disabled={isLoadingReport} style={{ marginTop: '20px' }}>
						{isLoadingReport ? 'Создание отчета...' : 'Отправить отчет'}
					</Button>
				</div>
		)}
		<div className={styles['section-item']}>
			{pageCount > 1 && <Paginate handlePageClick={handlePageClick} pageCount={pageCount} forcePage={currentPage} />}
		</div>
			<Popup isOpen={isPopupOpen} onClose={closePopup}>
				{loading2 && <p>Загрузка...</p>}
				{error && <p>{error}</p>}
				{data && data.length > 0 ? (
					<ul>
						{data.map(item => (
							<li key={item.cashier_login}>
								<p>
									<strong>Логин:</strong> {item.cashier_login}
								</p>
								<p>
									<strong>Пароль:</strong> {item.cashier_passw}
								</p>
							</li>
						))}
					</ul>
				) : (
					<p>Данные не найдены</p>
				)}
			</Popup>
		</>
	);
}
